import React, { useRef } from 'react';
import cn from 'classnames';
import Section from '../section/section';
import Icon from '../../../components/Atoms/icon/icon';
import { IconscoutIcons } from '../../../components/Atoms/icon/iconscout-icon';
import Avatar from '../../../components/avatar/avatar';
import TableOfContents, { type AnchorLink } from '../../../components/table-of-contents/table-of-contents';
import { useElementVisibility } from './use-element-visibility.hook';
import { useMediaQuery } from 'react-responsive';
import {
  ITestimonialSecondaryProps,
  TestimonialSecondary,
} from '../../../components/testimonial/testimonial-secondary/testimonial-secondary';

interface IArticleTextSectionProps {
  articleHtml: string;
  articleAnchors?: AnchorLink[];
  author?: {
    name: string;
    caption: string;
    image: { src: string; alt: string };
    position: string;
    linkedinLink: string;
  };
  testimonial?: ITestimonialSecondaryProps;
}

export default function ArticleTextSection({
  articleHtml,
  articleAnchors = [],
  author,
  testimonial,
}: IArticleTextSectionProps) {
  const isTabletOrMobile = useMediaQuery({ maxWidth: 768 });

  const articleRef = useRef<HTMLElement | null>(null);
  const showTOC = useElementVisibility(articleRef, '0px 0px -100% 0px', isTabletOrMobile);

  return (
    <section>
      <Section className="article-text-section">
        <div className="article-text-section__wrapper">
          <div className="article-text-section__content">
            <article
              ref={articleRef}
              className="article-text-section__article"
              dangerouslySetInnerHTML={{ __html: articleHtml }}
            />
            {author && (
              <a href={author.linkedinLink} target="_blank" rel="noreferrer" className="article-text-section__author">
                <div className="article-text-section__author__image">
                  <Avatar imgSrc={author.image.src} />
                </div>
                <div className="article-text-section__author__info">
                  <header>
                    <h5 className="article-text-section__author__info__name">{author.name}</h5>
                    <Icon icon={IconscoutIcons.LINKEDIN} />
                    <span className="article-text-section__author__info__position">{author.position}</span>
                  </header>
                  <footer>
                    <p className="article-text-section__author__info__caption">{author.caption}</p>
                  </footer>
                </div>
              </a>
            )}
          </div>

          {!isTabletOrMobile && (
            <aside className={cn('article-text-section__aside', { show: showTOC })}>
              <TableOfContents links={articleAnchors} />
              {testimonial && <TestimonialSecondary {...testimonial} />}
            </aside>
          )}
        </div>
      </Section>

      {isTabletOrMobile && testimonial && (
        <div className="article-text-section__testimonial">
          <TestimonialSecondary {...testimonial} />
        </div>
      )}
    </section>
  );
}
